import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { io } from "socket.io-client";
import {
  Box,
  Container,
  Typography,
  IconButton,
  TextField,
  CircularProgress,
  Stack,
  Skeleton,
  InputAdornment,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import TableChartRoundedIcon from "@mui/icons-material/TableChartRounded";
import { useSnackbar } from "notistack";
import DashboardHeaderSection from "../common/dashboardheader/DashboardHeader";
import GetCookieByName from "../../hooks/GetCookie";
import {
  useEmailInbox,
  useEmailAdminThread,
  useSendEmailAdminReply,
  useCurrentUserId,
  useInternalUsers,
  useMarkEmailRead,
  useSetEmailAiPaused,
} from "../../hooks/useEmail";
import {
  formatTime,
  formatDateLabel,
  getNumberColor,
  ChatListTabs,
  UnreadBadge,
  AiStatusChip,
  AiProgressBar,
  sortConversationsNewestFirst,
  mergeOpenedChats,
  setConversationUnread,
} from "../whatsapp/chatUiHelpers";
import EmailUserSwitcher from "./EmailUserSwitcher";
import { useDebounce } from "../../hooks/useDebounce";
import EmailAiControl from "./EmailAiControl";

// Gmail's own list-then-full-view paradigm: the list is a single full-width column of
// dense one-line rows (sender · subject — snippet · date), and clicking a row REPLACES the
// list with a full-width reading view (back arrow to return), rather than a permanent
// split pane — matches how Gmail desktop itself behaves, and the reference screenshot the
// user gave (a plain, dense Inbox list with no avatars/side panel).

function displayName(conv) {
  return conv?.rapidInfluencerId?.userName || conv?.rapidInfluencerId?.username || conv?.influencerEmail || "Unknown";
}

function initialOf(name) {
  return (name || "?").trim().charAt(0).toUpperCase() || "?";
}

function cleanSnippet(text) {
  return (text || "").replace(/\s+/g, " ").trim();
}

// Unread comes from the server (routes/emailAdmin.js): the creator's reply is the latest message AND
// the mailbox owner hasn't opened the thread since, tracked by the thread's shared read marker. The
// old "last message is inbound" guess is kept only as a fallback for a backend that doesn't send the
// flag yet, so the page still works if it is deployed first.
function isUnread(conv) {
  if (typeof conv.unread === "boolean") return conv.unread;
  const lastMsg = (conv.messages || [])[0];
  return Boolean(lastMsg && !lastMsg.isFromUs);
}

// The same rule applied to a full thread payload (the thread endpoint returns the whole document, so
// it carries lastReadAt): used to decide whether opening this thread should mark it read.
function isThreadUnread(conversation) {
  const messages = conversation?.messages || [];
  const last = messages[messages.length - 1];
  if (!last || last.isFromUs) return false;
  const readAt = conversation.lastReadAt ? new Date(conversation.lastReadAt).getTime() : 0;
  return new Date(last.sentAt).getTime() > readAt;
}

// Same icon-by-extension guess as EmailBulkSendModal.jsx's compose-side attachment chips —
// kept as its own small copy here rather than a shared import, since the two components
// render attachments in different contexts (an upload-in-progress chip vs. a
// already-sent, read-only file card).
function fileAccentColor(filename) {
  const ext = (filename || "").split(".").pop()?.toLowerCase();
  if (ext === "pdf") return "#D93025";
  if (["doc", "docx"].includes(ext)) return "#1A73E8";
  if (["xls", "xlsx", "csv"].includes(ext)) return "#188038";
  return "#5F6368";
}

function fileTypeIcon(filename, size = 20) {
  const ext = (filename || "").split(".").pop()?.toLowerCase();
  const color = fileAccentColor(filename);
  if (ext === "pdf") return <PictureAsPdfRoundedIcon sx={{ fontSize: size, color }} />;
  if (["doc", "docx"].includes(ext)) return <DescriptionRoundedIcon sx={{ fontSize: size, color }} />;
  if (["xls", "xlsx", "csv"].includes(ext)) return <TableChartRoundedIcon sx={{ fontSize: size, color }} />;
  return <InsertDriveFileOutlinedIcon sx={{ fontSize: size, color }} />;
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Gmail's own "(1 hour ago)" alongside the absolute time — coarse (minutes/hours/days) is
// plenty for a relative label; anything beyond a week just falls back to the exact date.
function relativeTime(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return "";
}

// One read-only attached-file card in the thread view — mirrors Gmail's own attachment
// tile: a large preview area on top (the real image for a picture, a big file-type icon
// otherwise — no thumbnail-generation pipeline for PDFs/docs, so a big icon is the honest
// stand-in), then a filename bar with a small type icon and a colored "folded corner"
// accent bottom-right, the same visual Gmail itself uses to hint at the file type.
function AttachmentCard({ attachment }) {
  const isImage = attachment.contentType?.startsWith("image/") || /\.(png|jpe?g|gif|webp|svg)$/i.test(attachment.filename || "");
  const accent = fileAccentColor(attachment.filename);

  return (
    <Box
      onClick={() => attachment.url && window.open(attachment.url, "_blank", "noopener,noreferrer")}
      sx={{
        width: 200,
        borderRadius: "8px",
        border: "1px solid rgba(0,0,0,0.12)",
        overflow: "hidden",
        bgcolor: "#fff",
        cursor: attachment.url ? "pointer" : "default",
        transition: "box-shadow 0.15s",
        "&:hover": attachment.url ? { boxShadow: "0 1px 6px rgba(0,0,0,0.18)" } : undefined,
      }}
    >
      <Box sx={{ height: 132, bgcolor: "#F1F3F4", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {isImage ? (
          <Box component="img" src={attachment.url} alt={attachment.filename} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          fileTypeIcon(attachment.filename, 48)
        )}
      </Box>
      <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 0.75, px: 1.1, py: 0.85 }}>
        {fileTypeIcon(attachment.filename, 15)}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontFamily: "Quicksand", fontSize: 12, fontWeight: 600, color: "#202124" }}>
            {attachment.filename}
          </Typography>
          <Typography noWrap sx={{ fontFamily: "Quicksand", fontSize: 10, color: "#80868B" }}>
            {formatBytes(attachment.size)}
          </Typography>
        </Box>
        {/* Gmail's folded-corner accent — a plain CSS triangle, colored by file type */}
        <Box
          sx={{
            position: "absolute",
            right: 0,
            bottom: 0,
            width: 0,
            height: 0,
            borderStyle: "solid",
            borderWidth: "0 0 13px 13px",
            borderColor: `transparent transparent transparent ${accent}`,
          }}
        />
      </Box>
    </Box>
  );
}

function formatListDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString("en-IN", sameYear ? { day: "numeric", month: "short", timeZone: "Asia/Kolkata" } : { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
}

// One dense, single-line row — sender in a fixed-width column, then "Subject — snippet" as
// one truncating line, date right-aligned. No avatar/side accents (the reference screenshot
// has none) — unread is carried entirely by weight + background, the way Gmail itself does.
function ConversationRow({ conv, onSelect }) {
  const lastMsg = (conv.messages || [])[0];
  const unread = isUnread(conv);
  const unreadMessages = conv.unreadMessages ?? (unread ? 1 : 0);
  const name = displayName(conv);
  const subject = lastMsg?.subject || "(no subject)";
  const snippet = lastMsg ? `${lastMsg.isFromUs ? "You: " : ""}${cleanSnippet(lastMsg.bodyText) || "(no content)"}` : "No messages yet";

  return (
    <Box
      onClick={() => onSelect(conv._id)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2.5,
        py: 1.1,
        cursor: "pointer",
        bgcolor: unread ? "#fff" : "#F1F3F4",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        "&:hover": { bgcolor: "#EDEFF1", boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)" },
      }}
    >
      <Typography
        noWrap
        sx={{ fontFamily: "Quicksand", fontWeight: unread ? 800 : 500, fontSize: 13.5, color: "#202124", width: 180, flexShrink: 0 }}
      >
        {name}
      </Typography>
      <Typography noWrap sx={{ fontFamily: "Quicksand", fontSize: 13, color: "#202124", flex: 1, minWidth: 0 }}>
        <Box component="span" sx={{ fontWeight: unread ? 700 : 500 }}>
          {subject}
        </Box>
        <Box component="span" sx={{ color: "#5F6368", fontWeight: 400 }}> — {snippet}</Box>
      </Typography>
      {lastMsg?.attachments?.length > 0 && <AttachFileRoundedIcon sx={{ fontSize: 15, color: "#80868B", flexShrink: 0 }} />}
      <AiStatusChip status={conv.qa?.status} paused={conv.qa?.paused} />
      <UnreadBadge count={unreadMessages} />
      <Typography sx={{ fontFamily: "Quicksand", fontSize: 11.5, color: unread ? "#25D366" : "#80868B", fontWeight: unread ? 700 : 400, flexShrink: 0, minWidth: 64, textAlign: "right" }}>
        {formatListDate(conv.lastMessageAt)}
      </Typography>
    </Box>
  );
}

// One collapsed/expandable card per message — Gmail always keeps the newest message open
// and collapses everything above it into a one-line summary, so a long thread stays
// scannable instead of dumping every prior message's full text on screen at once. Mirrors
// Gmail's own per-message header (name + email, "to X", relative + absolute time) and its
// attachment cards below the body when the message actually has any.
function EmailMessageCard({ msg, senderName, toLabel, defaultExpanded }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const fromName = msg.isFromUs ? msg.sentByName || "You" : senderName;
  const fromEmail = msg.sentByEmail || null;
  const color = getNumberColor(fromEmail || fromName);
  const rel = relativeTime(msg.sentAt);

  return (
    <Box sx={{ border: "1px solid rgba(0,0,0,0.09)", borderRadius: "10px", mb: 1.5, overflow: "hidden", bgcolor: "#fff" }}>
      <Box
        onClick={() => setExpanded((e) => !e)}
        sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.4, cursor: "pointer", "&:hover": { bgcolor: "#F8F9FA" } }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: "50%",
            bgcolor: color,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "Quicksand",
            fontWeight: 700,
            fontSize: 12.5,
            flexShrink: 0,
          }}
        >
          {initialOf(fromName)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontFamily: "Quicksand", fontWeight: 700, fontSize: 13, color: "#202124" }}>
            {fromName}
            {fromEmail && fromEmail !== fromName && (
              <Box component="span" sx={{ fontWeight: 400, color: "#5F6368" }}> &lt;{fromEmail}&gt;</Box>
            )}
            {msg.isAi && (
              <Box
                component="span"
                sx={{ ml: 1, px: 0.7, py: 0.1, borderRadius: "4px", bgcolor: "rgba(9,172,49,0.12)", color: "#09AC31", fontSize: 10, fontWeight: 800 }}
              >
                AI
              </Box>
            )}
          </Typography>
          {expanded && toLabel && (
            <Typography noWrap sx={{ fontFamily: "Quicksand", fontSize: 11, color: "#80868B" }}>
              to {toLabel}
            </Typography>
          )}
          {!expanded && (
            <Typography noWrap sx={{ fontFamily: "Quicksand", fontSize: 11.5, color: "#5F6368" }}>
              {cleanSnippet(msg.bodyText)}
            </Typography>
          )}
        </Box>
        {msg.attachments?.length > 0 && <AttachFileRoundedIcon sx={{ fontSize: 16, color: "#80868B", flexShrink: 0 }} />}
        <Typography sx={{ fontFamily: "Quicksand", fontSize: 11, color: "#80868B", flexShrink: 0, textAlign: "right" }}>
          {formatDateLabel(msg.sentAt)}, {formatTime(msg.sentAt)}
          {rel && expanded && (
            <Box component="span" sx={{ display: "block", fontSize: 10 }}>
              ({rel})
            </Box>
          )}
        </Typography>
        <IconButton size="small" sx={{ p: 0.3 }}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      {expanded && (
        <Box sx={{ px: 2, pb: 2, pt: 0.5, borderTop: "1px solid rgba(0,0,0,0.05)" }}>
          <Typography sx={{ fontFamily: "Quicksand", fontSize: 13.5, color: "#3C4043", whiteSpace: "pre-wrap", lineHeight: 1.65, mt: 1.2 }}>
            {msg.bodyText}
          </Typography>
          {msg.attachments?.length > 0 && (
            <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px solid rgba(0,0,0,0.05)" }}>
              <Typography sx={{ fontFamily: "Quicksand", fontSize: 11.5, color: "#5F6368", fontWeight: 700, mb: 1 }}>
                {msg.attachments.length} attachment{msg.attachments.length === 1 ? "" : "s"}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {msg.attachments.map((att, i) => (
                  <AttachmentCard key={att.url || i} attachment={att} />
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

// Gmail's reply affordance is collapsed by default (just a "Reply" pill) and only turns
// into an actual text box once clicked — keeps the reading pane from always ending in an
// open textarea even when you're just skimming replies.
function ReplyBox({ onSend, sending }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setText("");
    setOpen(false);
    onSend(trimmed);
  };

  if (!open) {
    return (
      <Box
        onClick={() => setOpen(true)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          border: "1px solid rgba(0,0,0,0.12)",
          borderRadius: "20px",
          px: 2,
          py: 0.9,
          cursor: "pointer",
          color: "#3C4043",
          "&:hover": { bgcolor: "#F1F3F4", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
        }}
      >
        <ReplyRoundedIcon fontSize="small" />
        <Typography sx={{ fontFamily: "Quicksand", fontWeight: 600, fontSize: 13 }}>Reply</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: "10px", bgcolor: "#fff" }}>
      <TextField
        fullWidth
        multiline
        minRows={4}
        maxRows={10}
        autoFocus
        placeholder="Type your reply…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        sx={{
          "& .MuiOutlinedInput-root": { fontFamily: "Quicksand", fontSize: 13.5, "& fieldset": { border: "none" } },
        }}
      />
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
        <Typography
          onClick={() => {
            setOpen(false);
            setText("");
          }}
          sx={{ fontFamily: "Quicksand", fontSize: 12.5, color: "#5F6368", cursor: "pointer", "&:hover": { color: "#202124" } }}
        >
          Discard
        </Typography>
        <IconButton
          onClick={handleSend}
          disabled={!text.trim() || sending}
          sx={{
            bgcolor: text.trim() ? "#1A73E8" : "rgba(0,0,0,0.06)",
            color: text.trim() ? "#fff" : "#9CA3AF",
            "&:hover": { bgcolor: text.trim() ? "#1A66D0" : "rgba(0,0,0,0.06)" },
            width: 38,
            height: 38,
          }}
        >
          {sending ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <SendRoundedIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Stack>
    </Box>
  );
}

// The full-width reading view a row click replaces the list with — subject as a page
// heading, a back arrow to return to the list, then the thread's messages and reply box.
// Replying always sends through the LOGGED-IN user's own impersonated Gmail mailbox (see
// helpers/emailBulkHelper.js) — POST .../reply 404s for anyone else's regardless, so this
// is purely a UI convenience (show/hide the box), never the actual access boundary.
function ThreadView({ conversationId, userId, currentUserId, viewingName, onBack, onSend, onMarkRead, onTogglePause, pausing, sending }) {
  const { data, isLoading, isError, refetch } = useEmailAdminThread(conversationId, userId);
  const conversation = data?.conversation;
  const senderName = displayName(conversation);
  const messages = conversation?.messages || [];
  const subject = messages[messages.length - 1]?.subject || "(no subject)";
  // The mailbox this specific conversation actually belongs to, read off its own outbound
  // messages (sentBy/sentByName denormalize the sending staff member — see models/
  // emailConversation.js) rather than the page-level switcher's selection. The two agree
  // when a specific person is selected, but the switcher carries no per-conversation owner
  // at all in "All users" mode — where, before this, every open thread showed a generic
  // placeholder instead of whoever it's actually with, and the reply box stayed hidden even
  // for a conversation that actually is the viewer's own.
  const conversationOwner = messages.find((m) => m.isFromUs && (m.sentByName || m.sentByEmail));
  const resolvedViewingName = conversationOwner?.sentByName || conversationOwner?.sentByEmail || viewingName;
  const isOwnInbox = Boolean(conversationOwner) && String(conversationOwner.sentBy) === String(currentUserId);

  // Viewing a thread in your OWN mailbox is reading it: mark it read (once per latest message — a new
  // reply that lands while it is open is read too). Someone else's mailbox, seen through the user
  // switcher, is never marked: that would clear THEIR unread state, and the server refuses it anyway.
  const lastMessageId = messages[messages.length - 1]?.messageId;
  const markedRef = useRef(null);
  useEffect(() => {
    if (!isOwnInbox || !conversation || !isThreadUnread(conversation)) return;
    const key = `${conversationId}:${lastMessageId}`;
    if (markedRef.current === key) return;
    markedRef.current = key;
    onMarkRead(conversationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwnInbox, conversation, conversationId, lastMessageId]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.5, borderBottom: "1px solid rgba(0,0,0,0.06)", flexShrink: 0 }}>
        <IconButton size="small" onClick={onBack}>
          <ArrowBackRoundedIcon fontSize="small" />
        </IconButton>
        {!isLoading && conversation && (
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontFamily: "Quicksand", fontWeight: 800, fontSize: 17, color: "#202124" }}>
              {subject}
            </Typography>
            <Typography sx={{ fontFamily: "Quicksand", fontSize: 11.5, color: "#5F6368" }}>
              {conversation.influencerEmail}
              {conversation.planId?.name ? ` · ${conversation.planId.name}` : ""}
            </Typography>
          </Box>
        )}
      </Box>

      {/* What the AI has collected so far, and (for your own mailbox) a switch to take the thread over. */}
      <AiProgressBar progress={conversation?.aiProgress} noun="email" pausing={pausing} onTogglePause={isOwnInbox ? onTogglePause : undefined} />

      <Box sx={{ flex: 1, overflowY: "auto", px: { xs: 2, sm: 3 }, py: 2.5, bgcolor: "#F6F8FA" }}>
        {isError ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, py: 6 }}>
            <Typography sx={{ fontFamily: "Quicksand", fontSize: 13, color: "#9CA3AF" }}>
              Couldn't load this conversation.
            </Typography>
            <Typography
              onClick={() => refetch()}
              sx={{ fontFamily: "Quicksand", fontSize: 12.5, fontWeight: 700, color: "#1A73E8", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
            >
              Try again
            </Typography>
          </Box>
        ) : isLoading || !conversation ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={i === 3 ? 140 : 56} sx={{ borderRadius: "10px" }} />
            ))}
          </Box>
        ) : (
          <Box sx={{ maxWidth: 760, mx: "auto" }}>
            {messages.map((msg, i) => (
              <EmailMessageCard
                key={msg.messageId || `msg-${i}`}
                msg={msg}
                senderName={senderName}
                toLabel={msg.isFromUs ? conversation.influencerEmail : resolvedViewingName || "you"}
                defaultExpanded={i === messages.length - 1}
              />
            ))}
            <Box sx={{ mt: 1 }}>
              {isOwnInbox ? (
                <ReplyBox onSend={onSend} sending={sending} />
              ) : (
                <Typography sx={{ fontFamily: "Quicksand", fontSize: 12.5, color: "#9CA3AF", fontStyle: "italic" }}>
                  Viewing {resolvedViewingName || "this user"}'s emails — replying is only available from your own inbox.
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function EmailInboxPage() {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const location = useLocation();
  const socketRef = useRef(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  // Seeded from the "View" action on a NotificationsListener.jsx toast (see its
  // navigate("/email/inbox", { state: { conversationId } })) — jumps straight into the
  // full reading view for that conversation instead of landing on the list.
  const [selectedConversationId, setSelectedConversationId] = useState(() => location.state?.conversationId || null);
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState("all");
  // Threads opened from the Unread tab this visit, id -> the row as it was shown (marked read) — kept in
  // the list until you leave the tab, see mergeOpenedChats.
  const [openedById, setOpenedById] = useState(() => new Map());
  const refreshTimerRef = useRef(null);

  // Each staff member sends/receives via their own impersonated Gmail mailbox, so the
  // Inbox is per-user with this switcher to view someone else's — defaults to yourself
  // once the roster/current-user id resolve (both start out null on first render).
  const currentUserId = useCurrentUserId();
  const { data: usersData } = useInternalUsers();
  const [selectedUserId, setSelectedUserId] = useState(null);
  useEffect(() => {
    if (!selectedUserId && currentUserId) setSelectedUserId(currentUserId);
  }, [currentUserId, selectedUserId]);
  const viewingUser = (usersData?.users || []).find((u) => String(u._id) === String(selectedUserId));
  const handleUserChange = (userId) => {
    setSelectedUserId(userId);
    setSelectedConversationId(null);
    setOpenedById(new Map());
    setPage(1);
  };

  // Opening the Inbox counts as "seen it" — reset the sidebar badge (see
  // NotificationsListener.jsx, which is what increments this same cache key).
  useEffect(() => {
    queryClient.setQueryData(["email-unread"], 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unreadOnly = tab === "unread";
  // The typed text is searched on the server (every thread in the mailbox, not just this page), after a short pause.
  const debouncedSearch = useDebounce(search.trim(), 350);
  // A new search starts from page 1, like switching tabs; threads kept on the Unread tab after being
  // opened are dropped too, or they would show up under a search they don't match (same as WhatsApp Inbox).
  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
    setOpenedById(new Map());
  };
  const { data, isLoading, refetch, isFetching } = useEmailInbox(page, selectedUserId, unreadOnly, debouncedSearch);
  const markReadMutation = useMarkEmailRead();
  const setAiPausedMutation = useSetEmailAiPaused();
  // `useEmailInbox` uses keepPreviousData for smooth pagination, but that also means a
  // switch from user A to user B briefly keeps rendering A's rows (under B's header)
  // until B's fetch resolves — clicking one of those in that window would open a
  // conversation that doesn't belong to B. Tracked separately from `isLoading` (which
  // only covers the very first fetch) so a user *switch* hides the stale list instead of
  // showing it, while an ordinary page change still benefits from keepPreviousData.
  const [loadedUserId, setLoadedUserId] = useState(selectedUserId);
  useEffect(() => {
    if (!isFetching) setLoadedUserId(selectedUserId);
  }, [isFetching, selectedUserId]);
  const listIsStale = loadedUserId !== selectedUserId;
  // Sorted here too (not just trusted from the API) because live updates edit the cached list in place.
  const conversations = useMemo(() => {
    const fromServer = data?.conversations || [];
    return unreadOnly ? mergeOpenedChats(fromServer, openedById) : sortConversationsNewestFirst(fromServer);
  }, [data, unreadOnly, openedById]);
  const unreadCount = data?.unreadCount || 0;
  // Search runs on the server (see debouncedSearch above), so the loaded list is already the matching one.
  const filteredConversations = conversations;
  const total = data?.total || 0;
  const limit = data?.limit || 30;
  const totalPages = Math.max(Math.ceil(total / limit), 1);
  const rangeStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = Math.min(page * limit, total);
  const replyMutation = useSendEmailAdminReply();

  // Flip the thread to read in every cached page first so its count and bold style clear at once, keep it
  // listed if we are on the Unread tab, then tell the server.
  const markRead = (conversationId) => {
    const row = conversations.find((c) => c.conversationId === conversationId);
    if (row?.unread) setOpenedById((prev) => new Map(prev).set(conversationId, { ...row, unread: false, unreadMessages: 0 }));
    queryClient.setQueriesData(["email-inbox"], (old) => setConversationUnread(old, conversationId, false));
    markReadMutation.mutate(conversationId);
  };

  // Replies can arrive in bursts, so the refetch that keeps the Unread tab and its badge honest is
  // debounced into one.
  const scheduleInboxRefresh = () => {
    if (refreshTimerRef.current) return;
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      queryClient.invalidateQueries({ queryKey: ["email-inbox"] });
    }, 800);
  };
  useEffect(() => () => clearTimeout(refreshTimerRef.current), []);

  // Live updates — routes/gmailWebhook.js broadcasts to EMAIL_INBOX_ROOM (every connected
  // admin), so this reaches every viewer regardless of whose mailbox the reply actually
  // landed in. Only invalidates the affected queries (the notification carries a snippet,
  // not the full message) rather than reconstructing it client-side.
  useEffect(() => {
    let authToken;
    try {
      authToken = JSON.parse(GetCookieByName("auth"))["token"];
    } catch (e) {
      return undefined;
    }

    const socket = io(new URL(process.env.REACT_APP_API_URL).origin, { auth: { token: authToken } });
    socketRef.current = socket;

    // Diagnostic — mirrors WhatsAppInboxPage.jsx's own connect/connect_error/disconnect
    // logging, added for the same reason: a "real-time not working" report with no
    // obvious cause in the code is fastest to resolve by seeing what the browser's own
    // socket actually did. Safe to leave in: low-noise, internal-only page.
    socket.on("connect", () => console.log("[email-inbox] socket connected", socket.id));
    socket.on("connect_error", (err) => console.error("[email-inbox] socket connect_error:", err.message));
    socket.on("disconnect", (reason) => console.warn("[email-inbox] socket disconnected:", reason));

    socket.on("email-reply-notify", ({ conversationId }) => {
      console.log("[email-inbox] email-reply-notify received", conversationId);
      queryClient.invalidateQueries({ queryKey: ["email-admin-thread", conversationId] });
      scheduleInboxRefresh();
    });

    // The mailbox owner (possibly in another tab) opened a thread — refresh our Unread tab and badge.
    socket.on("email-read-update", () => scheduleInboxRefresh());

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);

  const handleSend = async (message) => {
    setSending(true);
    try {
      const res = await replyMutation.mutateAsync({ conversationId: selectedConversationId, message });
      if (!res?.success) throw new Error(res?.message || "Failed to send reply");
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || error.message || "Failed to send reply", { variant: "error" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)" }}>
      <DashboardHeaderSection hideSearch />
      <Container sx={{ marginTop: 8, pb: 6, pl: { xs: 2, sm: 3, lg: "108px" }, pr: { xs: 2, sm: 3 } }} maxWidth="xl">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1.5 }}>
          <Typography sx={{ fontFamily: "Quicksand", fontWeight: 800, fontSize: 22, display: "flex", alignItems: "center", gap: 1 }}>
            <MailOutlineIcon sx={{ color: "#1A73E8" }} /> Email Inbox
          </Typography>
          <Stack direction="row" alignItems="center" spacing={2.5} flexWrap="wrap">
            {/* Per-mailbox AI switch: the AI replies from the selected mailbox's own address. */}
            <EmailAiControl userId={selectedUserId} userLabel={viewingUser?.fullName || viewingUser?.email} />
            <EmailUserSwitcher selectedUserId={selectedUserId} currentUserId={currentUserId} onChange={handleUserChange} />
          </Stack>
        </Box>

        <Box
          sx={{
            border: "1px solid rgba(0,0,0,0.07)",
            borderRadius: "16px",
            overflow: "hidden",
            height: "calc(100vh - 260px)",
            minHeight: 520,
            boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
            bgcolor: "#fff",
          }}
        >
          {selectedConversationId ? (
            <ThreadView
              conversationId={selectedConversationId}
              userId={selectedUserId}
              currentUserId={currentUserId}
              viewingName={viewingUser?.fullName || viewingUser?.email}
              onBack={() => setSelectedConversationId(null)}
              onSend={handleSend}
              onMarkRead={markRead}
              pausing={setAiPausedMutation.isLoading}
              onTogglePause={(paused) =>
                setAiPausedMutation.mutate(
                  { conversationId: selectedConversationId, paused },
                  { onError: (error) => enqueueSnackbar(error?.response?.data?.message || "Could not change AI pause", { variant: "error" }) }
                )
              }
              sending={sending}
            />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
              <ChatListTabs
                tab={tab}
                onChange={(value) => {
                  setTab(value);
                  setPage(1);
                  setOpenedById(new Map()); // leaving the tab drops the threads kept visible after being opened
                }}
                unreadCount={unreadCount}
              />
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.25, borderBottom: "1px solid rgba(0,0,0,0.06)", flexShrink: 0 }}>
                <TextField
                  size="small"
                  placeholder="Search name, email or subject"
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ fontSize: 18, color: "#80868B" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: 1,
                    maxWidth: 420,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "20px",
                      fontFamily: "Quicksand",
                      fontSize: 13,
                      bgcolor: "#F1F3F4",
                      "& fieldset": { border: "none" },
                    },
                  }}
                />
                <Box sx={{ flex: 1 }} />
                <IconButton size="small" onClick={() => refetch()} disabled={isFetching}>
                  <RefreshRoundedIcon fontSize="small" sx={{ animation: isFetching ? "spin 0.8s linear infinite" : "none", "@keyframes spin": { to: { transform: "rotate(360deg)" } } }} />
                </IconButton>
                <Typography sx={{ fontFamily: "Quicksand", fontSize: 12, color: "#5F6368", whiteSpace: "nowrap" }}>
                  {total === 0 ? "0 of 0" : `${rangeStart}–${rangeEnd} of ${total}`}
                </Typography>
                <IconButton size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Stack>

              <Box sx={{ flex: 1, overflowY: "auto" }}>
                {isLoading || listIsStale ? (
                  <Box sx={{ p: 2 }}>
                    {[...Array(10)].map((_, i) => (
                      <Skeleton key={i} variant="rounded" height={40} sx={{ mb: 1, borderRadius: "6px" }} />
                    ))}
                  </Box>
                ) : filteredConversations.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography sx={{ fontFamily: "Quicksand", color: "#9CA3AF", fontSize: 13 }}>
                      {debouncedSearch
                        ? `No emails match "${debouncedSearch}".`
                        : unreadOnly
                          ? "No unread emails — you're all caught up."
                          : "No email conversations yet — send an email to an influencer from a Plan's Summary page to start one."}
                    </Typography>
                  </Box>
                ) : (
                  filteredConversations.map((conv) => (
                    <ConversationRow key={conv._id} conv={conv} onSelect={setSelectedConversationId} />
                  ))
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}

export default EmailInboxPage;
